// Libs
import classNames from 'classnames/bind';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import {
  BaseUploadImage,
  BaseInput,
  BaseInputAddress,
  BaseLabel,
  BaseSelect,
  BaseToolbar,
  Loading,
  BaseDynamicSelect,
} from '~/components/common';
// Others
import { defaultValuesUserForm, schema } from './helper';
import { IFormUser } from '~/utils/interface/user';
import { useAppDispatch } from '~/utils/redux/hooks';
import { createUser, getDetailUser, updateUser } from '~/thunks/user/userThunk';
import {
  DOLLAR,
  EMPTY_STRING,
  optionTimezone,
  DEFAULT_LIMIT_PAGE,
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
} from '~/utils/constants/common';
import {
  IBaseAddress,
  IBaseFile,
  IBaseOption,
  IBodyUpdate,
  IPayloadCallApiFn,
  IQueryParamBase,
  IResultCallApiFn,
} from '~/utils/interface/common';
import {
  createFormData,
  formatInputCurrency,
  formatPhoneNumber,
  getChangedDataForm,
  isEmptyObject,
  removeEmptyObjects,
  removeNonDigits,
} from '~/utils/helper/common';
import { getListRole } from '~/thunks/role/roleThunk';
// Styles, images, icons
import styles from './UserForm.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const UserForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/users', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: userId } = useParams();

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty, dirtyFields, isSubmitted },
  } = useForm<IFormUser>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesUserForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<IFormUser>(defaultValuesUserForm);
  const [deleteAvatar, setDeleteAvatar] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!userId) return;

    handleGetDetailUser(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailUser = (userId: string) => {
    setIsLoading(true);

    dispatch(getDetailUser(userId))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, avatarImage, phoneNumber, ...remainingData } = res?.data;
        const body: IFormUser = {
          ...remainingData,
          countryCode: remainingData.countryCode ?? EMPTY_STRING,
          address: remainingData.address ?? EMPTY_STRING,
          unitDetail: remainingData.unitDetail ?? EMPTY_STRING,
          city: remainingData.city ?? EMPTY_STRING,
          state: remainingData.state ?? EMPTY_STRING,
          postalCode: remainingData.postalCode ?? EMPTY_STRING,
          country: remainingData.country ?? EMPTY_STRING,
          lat: remainingData.lat ?? EMPTY_STRING,
          roleIds: remainingData?.role?.map((item) => item?.id || EMPTY_STRING) ?? [],
          lng: remainingData.lng ?? EMPTY_STRING,
          phoneNumber: phoneNumber ? formatPhoneNumber(phoneNumber) : EMPTY_STRING,
          avatarImage: avatarImage ? { url: avatarImage } : {},
          timezone: remainingData.timezone ?? EMPTY_STRING,
          rate: remainingData?.rate || 0,
        };

        setInitialData(body);
        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormUser = (data: IFormUser) => {
    if (userId) {
      handleEditUser(data);
    } else {
      handleAddUser(data);
    }

    setIsLoading(true);
  };

  const handleAddUser = async (data: IFormUser) => {
    const newData: IFormUser = {
      ...data,
      phoneNumber: removeNonDigits(data?.phoneNumber),
    };

    const formData = await createFormData(removeEmptyObjects(newData), ['avatarImage']);

    dispatch(createUser(formData))
      .unwrap()
      .then((_res) => {
        handleCancel();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditUser = async (data: IFormUser) => {
    // const dataChange = compareDataUpdate(initialData, data) as IFormUser;
    //

    const cleanData = getChangedDataForm(dirtyFields, data) as IFormUser;

    const formatData: IFormUser = {
      ...cleanData,
      avatarImage: dirtyFields.avatarImage ? data.avatarImage : undefined,
      isDeleteAvatar: dirtyFields.avatarImage && !data.avatarImage?.file ? deleteAvatar : false,
      phoneNumber: cleanData?.phoneNumber && removeNonDigits(cleanData?.phoneNumber),
    };

    const formData = await createFormData(formatData, ['avatarImage']);

    const params: IBodyUpdate<FormData> = {
      id: userId || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateUser(params))
      .unwrap()
      .then(() => {
        handleCancel();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChangeAddress = (value: IBaseAddress) => {
    setValue('address', value.address ?? EMPTY_STRING, { shouldDirty: true });
    setValue('unitDetail', value.unitDetail ?? EMPTY_STRING, { shouldDirty: true });
    setValue('city', value.city ?? EMPTY_STRING, { shouldDirty: true });
    setValue('state', value.state ?? EMPTY_STRING, { shouldDirty: true });
    setValue('postalCode', value.postalCode ?? EMPTY_STRING, { shouldDirty: true });
    setValue('country', value.country ?? EMPTY_STRING, { shouldDirty: true });
    setValue('lat', String(value?.lat ?? 0), { shouldDirty: true });
    setValue('lng', String(value?.lng ?? 0), { shouldDirty: true });
  };

  const fetchOptionsRole = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamBase = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListRole(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: item?.name,
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionsTimezone = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTimezone,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleCancel = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };
  //#endregion Handle Function

  return (
    <Box id='userFormPage' className={cx('container')}>
      <BaseToolbar title={t(userId ? 'user_form_title_edit_user' : 'user_form_title_add_user')} isBack>
        <Button
          variant='contained'
          className={cx('buttonSendQuote')}
          onClick={handleSubmit(handleFormUser)}
          disabled={userId ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </BaseToolbar>

      <Stack className={cx('body')}>
        <BaseLabel styleType='title' label={t('user_form_title_edit_information')} />

        <Grid2 container columnSpacing={'10px'} rowSpacing={'12px'}>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'firstName'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    label={t('user_form_title_first_name')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.firstName?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'lastName'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    label={t('user_form_title_last_name')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.lastName?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'avatarImage'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseUploadImage
                    label={t('user_form_title_photo')}
                    {...(userId && !value?.file && { uploadedFiles: [value || {}] })}
                    onChange={(files: IBaseFile[]) => onChange(files?.[0])}
                    errorMessage={errors.avatarImage?.message}
                    isMultiple={false}
                    onRemove={() => setDeleteAvatar(true)}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'email'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    label={t('user_form_title_email')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.email?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'phoneNumber'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    label={t('user_form_title_phone')}
                    value={value}
                    onChange={(event) => {
                      onChange(formatPhoneNumber(event.target.value));
                    }}
                    errorMessage={errors?.phoneNumber?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'roleIds'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseDynamicSelect
                    isSearch
                    multiple={true}
                    label={t('user_form_title_role')}
                    callApiFn={fetchOptionsRole}
                    value={value}
                    onChange={(selected) => {
                      onChange(selected.idSelected);
                    }}
                    required
                    errorMessage={errors?.roleIds?.message}
                  />

                  // <BaseSelect
                  //   gap={10}
                  //   styleType='contained'
                  //   label={t('user_form_title_role')}
                  //   callApiFn={fetchOptionsRole}
                  //   value={value[0]}
                  //   onChange={(data) => onChange([data?.value])}
                  //   errorMessage={errors?.role?.message}
                  //   required
                  // />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'timezone'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={8}
                    height={40}
                    styleType='contained'
                    label={t('user_form_title_timezone')}
                    callApiFn={fetchOptionsTimezone}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.timezone?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'address'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInputAddress
                    label={t('user_form_title_address')}
                    value={value}
                    onChange={onChange}
                    onSelectAddress={handleChangeAddress}
                    errorMessage={errors?.address?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'city'}
              render={({ field: { onChange, value } }) => {
                return <BaseInput label={t('user_form_title_city')} value={value} onChange={onChange} />;
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'state'}
              render={({ field: { onChange, value } }) => {
                return <BaseInput label={t('user_form_title_state')} value={value} onChange={onChange} />;
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'postalCode'}
              render={({ field: { onChange, value } }) => {
                return <BaseInput label={t('user_form_title_zip_code')} value={value} onChange={onChange} />;
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'rate'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    type={'number'}
                    height={40}
                    label={t('user_form_title_rate')}
                    value={String(value)}
                    startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
                    onChange={(event) => {
                      onChange(formatInputCurrency(event?.target?.value));
                    }}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </Stack>

      {isLoading && <Loading />}
    </Box>
  );
};

export default UserForm;
